import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import {
  MagnifyingGlassIcon,
  PencilSimpleIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { z } from 'zod';
import {
  DataManagementService,
  type CompanyRecord,
  type ProposedDataChange,
} from '@/api/data-management-service';
import { ApiError } from '@/api/api-client';
import { useDebounce } from '@/hooks/use-debounce';
import { PageHeader } from '@/components/page-header';
import { PageContent } from '@/components/page-content';
import { DataTable } from '@/components/ui/data-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormDialog } from '@/components/ui/form-dialog';
import { ConfirmModal } from '@/components/ui/confirm-modal';
import { RowActionsMenu } from '@/components/ui/row-actions-menu';
import { useAppForm } from '@/hooks/use-app-form';

export const Route = createFileRoute(
  '/_auth/_side-layout/super-admin/data/companies',
)({
  component: SuperAdminCompaniesPage,
  beforeLoad: async ({ context }) => {
    if (context.platformRole !== 'admin') {
      throw redirect({ to: '/' });
    }
  },
});

// `company_enrichment_source` is a ~22M-row global table, so search is
// server-side and gated. The backend's substring `ILIKE '%q%'` is served by
// pg_trgm GIN indexes (migration 0049), which only kick in once the term has an
// extractable trigram — i.e. 3+ characters. Below that we'd fall back to a full
// scan, so don't fire the (debounced) query until the term is long enough.
const MIN_SEARCH_LENGTH = 3;

// Mirrors the backend `searchCompanies` default limit (data-management.ts). When
// a result set hits this we hint that the search is truncated, not exhaustive.
const RESULT_LIMIT = 25;

// The snake_case `proposed` payload for a attributes edit.
type CompanyProposed = {
  name: string;
  attributes: Record<string, unknown>;
};

function CompanyEditForm({
  company,
  onClose,
}: {
  company: CompanyRecord;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [confirmDirectOpen, setConfirmDirectOpen] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);

  const onChangeApplied = (result: ProposedDataChange | { applied: true }) => {
    void queryClient.invalidateQueries({ queryKey: ['data-changes'] });
    void queryClient.invalidateQueries({ queryKey: ['attributes'] });
    if ('applied' in result) {
      toast.success('Change applied to the company record.');
    } else {
      toast.success(
        result.change_kind === 'silent'
          ? 'Silent fix applied.'
          : 'Change staged for review.',
      );
    }
    onClose();
  };

  const onChangeError = (error: unknown) => {
    const message =
      error instanceof ApiError ? error.message : 'Failed to submit change.';
    toast.error(message);
  };

  const mutation = useMutation({
    mutationFn: (input: {
      proposed: CompanyProposed;
      change_kind: 'material' | 'silent';
      apply_direct?: boolean;
    }) =>
      DataManagementService.propose({
        entity_type: 'company_enrichment',
        entity_key: company.domain,
        scope: 'global',
        change_kind: input.change_kind,
        proposed: input.proposed,
        apply_direct: input.apply_direct,
      }),
    onSuccess: onChangeApplied,
    onError: onChangeError,
  });

  // Which write the next submit performs. Set just before form.handleSubmit()
  // so every button runs the SAME validators (no skipping via form.state.values).
  const submitMode = useRef<{
    change_kind: 'material' | 'silent';
    apply_direct?: boolean;
  }>({ change_kind: 'material' });

  const form = useAppForm({
    defaultValues: {
      name: company.name,
      attributes: JSON.stringify(company.attributes, null, 2),
    },
    validators: {
      onSubmit: z.object({
        name: z.string().trim().min(1, 'Name is required'),
        attributes: z.string(),
      }),
    },
    onSubmit: async ({ value }) => {
      // JSON shape is validated imperatively (inline error) since it can't be a
      // simple field validator.
      const proposed = buildProposed(value);
      if (proposed)
        mutation.mutate({
          proposed,
          change_kind: submitMode.current.change_kind,
          apply_direct: submitMode.current.apply_direct,
        });
    },
  });

  // Parses the JSON textarea, setting an inline error on failure. Returns the
  // proposed payload, or `null` if the attributes JSON is invalid.
  const buildProposed = (value: {
    name: string;
    attributes: string;
  }): CompanyProposed | null => {
    let parsed: Record<string, unknown>;
    try {
      const result = JSON.parse(value.attributes) as unknown;
      if (
        typeof result !== 'object' ||
        result === null ||
        Array.isArray(result)
      ) {
        throw new Error('Attributes must be a JSON object.');
      }
      parsed = result as Record<string, unknown>;
    } catch (error) {
      setJsonError(error instanceof Error ? error.message : 'Invalid JSON.');
      return null;
    }
    setJsonError(null);
    return { name: value.name, attributes: parsed };
  };

  // Set the write mode, then submit through the validated path.
  const requestSubmit = (
    change_kind: 'material' | 'silent',
    apply_direct?: boolean,
  ) => {
    submitMode.current = { change_kind, apply_direct };
    void form.handleSubmit();
  };

  return (
    <FormDialog open onOpenChange={(open) => !open && onClose()} size="lg">
      <FormDialog.Header>
        <FormDialog.Title>Edit Company Record</FormDialog.Title>
        <FormDialog.Description>
          {company.domain} — propose a change, apply a silent fix, or write
          live.
        </FormDialog.Description>
      </FormDialog.Header>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          requestSubmit('material');
        }}
      >
        <FormDialog.Content>
          <form.AppForm>
            <form.AppField name="name">
              {(field) => <field.Input label="Name" required />}
            </form.AppField>
            <form.AppField name="attributes">
              {(field) => (
                <field.Textarea
                  label="Attributes (JSON)"
                  description="The curated attributes object. Must be valid JSON."
                  className="min-h-64 font-mono text-xs"
                  autosize={false}
                />
              )}
            </form.AppField>
            {jsonError && (
              <p className="text-destructive text-sm">{jsonError}</p>
            )}
          </form.AppForm>
        </FormDialog.Content>
        <FormDialog.Footer className="flex-wrap justify-between gap-2">
          <Button
            type="button"
            variant="destructive"
            disabled={mutation.isPending}
            onClick={() => setConfirmDirectOpen(true)}
          >
            Apply Directly (Bypass Review)
          </Button>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={mutation.isPending}
              onClick={() => requestSubmit('silent')}
            >
              Silent Fix
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={mutation.isPending}
            >
              Propose Change
            </Button>
          </div>
        </FormDialog.Footer>
      </form>
      <ConfirmModal
        open={confirmDirectOpen}
        variant="destructive"
        title="Apply Directly?"
        message="This bypasses staging and review, writing the change live to the company record immediately. Intended for early testing only."
        confirmText="Apply Live Now"
        loading={mutation.isPending}
        onCancel={() => setConfirmDirectOpen(false)}
        onFinish={() => {
          setConfirmDirectOpen(false);
          requestSubmit('material', true);
        }}
      />
    </FormDialog>
  );
}

function buildColumns(
  onEdit: (company: CompanyRecord) => void,
  onDelete: (company: CompanyRecord) => void,
): ColumnDef<CompanyRecord>[] {
  return [
    {
      id: 'domain',
      header: 'Domain',
      cell: ({ row }) => (
        <span className="font-medium">{row.original.domain}</span>
      ),
    },
    {
      id: 'name',
      header: 'Name',
      cell: ({ row }) => row.original.name,
    },
    {
      id: 'version',
      header: 'Version',
      cell: ({ row }) => (
        <Badge variant="neutral">v{row.original.version}</Badge>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex justify-end">
          <RowActionsMenu
            actions={[
              {
                label: 'Edit',
                icon: <PencilSimpleIcon size={16} />,
                onSelect: () => onEdit(row.original),
              },
              {
                label: 'Delete',
                icon: <TrashIcon size={16} />,
                destructive: true,
                onSelect: () => onDelete(row.original),
              },
            ]}
          />
        </div>
      ),
    },
  ];
}

// A single curated attribute value rendered for the read-only expander:
// primitives as text, everything else as compact JSON.
function formatAttributeValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value === '' ? '—' : value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
}

// Read-only view of a company's curated attributes, shown in the expanded row
// so a super-admin can inspect the full record without opening the Edit dialog.
function CompanyAttributes({ company }: { company: CompanyRecord }) {
  const entries = Object.entries(company.attributes ?? {});
  if (entries.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        No attributes recorded for this company.
      </p>
    );
  }
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
      {entries.map(([key, value]) => (
        <div key={key} className="min-w-0">
          <dt className="text-muted-foreground text-xs font-medium">{key}</dt>
          <dd className="wrap-break-word text-sm">
            {formatAttributeValue(value)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

// Super-admin search + propose for curated company attributes. Search is
// query-driven (server-side); editing a row stages a proposal.
function CompaniesAdmin() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, 300);
  const query = debounced.trim();
  const [editing, setEditing] = useState<CompanyRecord | null>(null);
  const [deleting, setDeleting] = useState<CompanyRecord | null>(null);

  const { data: companies = [], isLoading } = useQuery({
    queryKey: ['attributes', query],
    queryFn: () => DataManagementService.searchCompanies(query),
    enabled: query.length >= MIN_SEARCH_LENGTH,
    staleTime: 1000 * 60,
  });

  // Deleting a company record 409s when it's still attached to a customer
  // account; the backend message surfaces via toast (the attached protection).
  const deleteMutation = useMutation({
    mutationFn: (domain: string) =>
      DataManagementService.deleteCompany(domain),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['attributes'] });
      toast.success('Company record deleted.');
      setDeleting(null);
    },
    onError: (error) => {
      const message =
        error instanceof ApiError
          ? error.message
          : 'Failed to delete company record.';
      toast.error(message);
      setDeleting(null);
    },
  });

  const columns = useMemo(
    () =>
      buildColumns(
        (company) => setEditing(company),
        (company) => setDeleting(company),
      ),
    [],
  );

  // Empty-state content for the (always-rendered) table; the column headers stay
  // visible as a search affordance. The "searching" state is handled by the
  // DataTable skeleton (isLoading), so this only covers pre-search vs no-match.
  const isPreSearch = query.length < MIN_SEARCH_LENGTH;
  const emptyMessage = isPreSearch
    ? 'Search Company Records'
    : 'No Matching Records';
  const emptyDescription = isPreSearch
    ? `Enter at least ${MIN_SEARCH_LENGTH} characters to search by domain or name.`
    : `No curated company records match “${query}”.`;

  return (
    <>
      <div className="max-w-sm">
        <Input
          placeholder="Search by domain or name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className="space-y-3">
        {companies.length > 0 && (
          <p className="text-muted-foreground text-sm">
            {companies.length === RESULT_LIMIT
              ? `Showing the first ${RESULT_LIMIT} matches — refine your search to narrow it down.`
              : `${companies.length} ${companies.length === 1 ? 'result' : 'results'}`}
          </p>
        )}
        <DataTable
          columns={columns}
          data={companies}
          manualPagination
          isLoading={isLoading}
          emptyIcon={MagnifyingGlassIcon}
          emptyMessage={emptyMessage}
          emptyDescription={emptyDescription}
          expandableRowRender={(company) => (
            <CompanyAttributes company={company} />
          )}
        />
      </div>
      {editing && (
        <CompanyEditForm
          key={editing.domain}
          company={editing}
          onClose={() => setEditing(null)}
        />
      )}
      <ConfirmModal
        open={Boolean(deleting)}
        variant="destructive"
        title="Delete Company Record?"
        message={
          deleting
            ? `Delete the curated record for ${deleting.domain}? This cannot be undone.`
            : ''
        }
        confirmText="Delete"
        loading={deleteMutation.isPending}
        onCancel={() => setDeleting(null)}
        onFinish={() => {
          if (deleting) deleteMutation.mutate(deleting.domain);
        }}
      />
    </>
  );
}

function SuperAdminCompaniesPage() {
  return (
    <>
      <PageHeader
        title="Company Data"
        description="Search and propose changes to curated company records (attributes)."
      />
      <PageContent>
        <CompaniesAdmin />
      </PageContent>
    </>
  );
}
